import { send } from '../../../../../../src/network/network';
import { type OrderList } from '../../../../../../src/types/OrderList';
import {
  type PaginatedOrderListList,
  type PaginatedOrderListWithSideloadedTags,
} from '../../../../../../src/types/PaginatedOrderListList';
import { convertTagIdsToTags } from '../../../../../../src/utils/convertTagIdsToTags';

type TimeBucket = 'past' | 'current' | 'upcoming';

export interface Params {
  preferred_date_after?: string;
  time_bucket?: TimeBucket;
  limit?: number;
}

export async function getCustomerOrders(params: Params) {
  const { preferred_date_after, limit, time_bucket } = params;
  const query = new URLSearchParams();

  if (preferred_date_after !== undefined) {
    query.set('preferred_date_after', preferred_date_after);
  }

  if (limit !== undefined) {
    query.set('limit', limit.toString());
  }

  if (time_bucket !== undefined) {
    query.set('time_bucket', time_bucket);
  }

  query.set('sideload', 'tags');

  const resp = await send<PaginatedOrderListWithSideloadedTags>({
    path: '/api/v2/customers/self/orders/',
    method: 'GET',
    query,
  });

  // This is for TS to indicate that we're returning a PaginatedOrderListList if we get an error response
  // b/c by default it thinks we're returning a PaginatedOrderListWithSideloadedTags
  if (resp.data?.results?.order?.length === 0) {
    const data: PaginatedOrderListList = {
      ...resp.data,
      results: [],
    };

    return {
      status: resp.status,
      data,
    };
  }

  const tags = resp.data.results.tags;

  const mappedOrders: OrderList[] = resp.data.results.order.map((order) => {
    // For each order, iterate over bundles, products, and promo_product which all have tags as ids
    // We find the tag object from the sideloaded tags array and replace the tagId with the full tag object

    return {
      ...order,
      bundles: order.bundles.map((bundle) => {
        return {
          ...bundle,
          pairing:
            bundle.pairing === null
              ? null
              : {
                  ...bundle.pairing,
                  ingredients: bundle.pairing.ingredients.map((ingredient) => {
                    return {
                      ...ingredient,
                      tags: convertTagIdsToTags(ingredient.tags || [], tags),
                    };
                  }),
                  tags: convertTagIdsToTags(bundle.pairing.tags, tags),
                },
        };
      }),
      products: order.products.map((product) => {
        return {
          ...product,
          tags: convertTagIdsToTags(product.tags, tags),
        };
      }),
      promo_product: order.promo_product
        ? {
            ...order.promo_product,
            tags: convertTagIdsToTags(order.promo_product.tags, tags),
          }
        : null,
    };
  });

  const data: PaginatedOrderListList = {
    count: resp.data.count,
    next: resp.data.next,
    previous: resp.data.previous,
    results: mappedOrders,
  };

  return {
    status: resp.status,
    data,
  };
}
